import { SasTokenPrefix } from "@services/user/types";

export const TERMINAL_SERVICE_DOCUMENT_PREFIXES = [SasTokenPrefix.PORT_SERVICES] as const;

export const FottServiceType = Object.freeze({
  GANTRY_CRANE_SERVICE: "GANTRY_CRANE_SERVICE",
  NITROGEN_SUPPLY_SERVICE: "NITROGEN_SUPPLY_SERVICE",
  PIGGING_SERVICE: "PIGGING_SERVICE",
  BYPASS_SERVICE: "BYPASS_SERVICE",
} as const);

export type FottServiceType = (typeof FottServiceType)[keyof typeof FottServiceType];

export const MatrixManifold = Object.freeze({
  MM1_EAST: "MM1_EAST",
  MM1_WEST: "MM1_WEST",
  MM2_NORTH: "MM2_NORTH",
  MM2_SOUTH: "MM2_SOUTH",
} as const);

export type MatrixManifold = (typeof MatrixManifold)[keyof typeof MatrixManifold];

export const PiggingProductType = Object.freeze({
  BLACK: "BLACK",
  WHITE: "WHITE",
} as const);

export type PiggingProductType = (typeof PiggingProductType)[keyof typeof PiggingProductType];

export const BypassOperationType = Object.freeze({
  LOADING: "LOADING",
  DISCHARGING: "DISCHARGING",
} as const);

export type BypassOperationType = (typeof BypassOperationType)[keyof typeof BypassOperationType];

export const FottServiceRequestStatus = Object.freeze({
  PENDING_APPROVAL: "PENDING_APPROVAL",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
  CLOSED: "CLOSED",
} as const);

export type FottServiceRequestStatus =
  (typeof FottServiceRequestStatus)[keyof typeof FottServiceRequestStatus];

export interface TerminalServiceDocument {
  documentUrl: string;
  documentName?: string | undefined;
  documentType?: string;
  otherDocumentType?: string | null;
  issueDate?: string | null;
  expirationDate?: string | null;
  documentSummary?: string | null;
}

export interface RejectTerminalServiceRequestPayload {
  status: typeof FottServiceRequestStatus.REJECTED;
  rejectionNotes: string;
  rejectionDocument?: TerminalServiceDocument | null;
  createdBy?: string;
  createdByRole?: string;
  createdAt?: string;
}

export interface ApproveTerminalServiceRequestPayload {
  status: typeof FottServiceRequestStatus.APPROVED;
  jobType: string;
  operatorName?: string;
  approvalNotes?: string;
  bookingConfirmation: TerminalServiceDocument;
  createdBy?: string;
  createdByRole?: string;
  createdAt?: string;
}

export interface CancelTerminalServiceRequestPayload {
  reason: string;
  attachment?: TerminalServiceDocument | null;
}

export interface TerminalServiceCancellationDetails {
  reason?: string | null;
  attachment?: TerminalServiceDocument | null;
  createdBy?: string | null;
  createdAt?: string | null;
}

export interface CloseBypassServiceRequestPayload {
  remarks?: string;
  attachment?: TerminalServiceDocument | null;
}

export interface SubmitTerminalServiceUsageHoursPayload {
  startTime: string;
  endTime: string;
  pressureInBar?: number;
}

export interface TerminalServiceTaskDecision {
  status?: FottServiceRequestStatus | string;
  rejectionNotes?: string | null;
  rejectionDocument?: TerminalServiceDocument | null;
  jobType?: string | null;
  operatorName?: string | null;
  approvalNotes?: string | null;
  bookingConfirmation?: TerminalServiceDocument | null;
  cancellationReason?: string | null;
  cancellationDocument?: TerminalServiceDocument | null;
  closureRemarks?: string | null;
  closureAttachment?: TerminalServiceDocument | null;
  createdBy?: string | null;
  createdByRole?: string | null;
  createdAt?: string | null;
}

export type BYPASS_DOCUMENT_TYPES =
  | "CERTIFICATE"
  | "PERMIT"
  | "DOCUMENT"
  | "INSURANCE"
  | "VESSEL_MASTER_SOF"
  | "OTHERS";

export interface BypassServiceDocument {
  documentType: BYPASS_DOCUMENT_TYPES;
  otherDocumentType?: string;
  documentUrl: string;
  documentName: string;
  issueDate?: string;
  expirationDate?: string;
}
export type PiggingServiceDocument = BypassServiceDocument;
export type NitrogenSupplyServiceDocument = BypassServiceDocument;
export type GantryCraneAdditionalServiceDocuments = BypassServiceDocument;

export interface TerminalServiceCompany {
  companyCode: string;
  companyNameEnglish: string;
}

export type TerminalServiceCompanyFilterKey = "isVendor" | "isOilCompany";

export interface BerthLocation {
  berthCode: string;
  berthName: string;
}

export interface TerminalServiceTask {
  taskId?: string;
  workflowId?: string;
  runId?: string;
  role?: string;
  createdAt?: string;
  status?: string;
  claimOwnerName?: string | null;
}

export interface TerminalServiceUsageEntry {
  id: string;
  entityId?: string;
  entityType?: FottServiceType | string;
  startTime: string;
  endTime: string;
  usageHours?: number;
  pressureInBar?: number | null;
  createdAt?: string | null;
  createdBy?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
}

export interface GantryCraneServiceDetailsData extends Record<string, unknown> {
  id?: string;
  status?: FottServiceRequestStatus | string;
  bookingFrom?: string;
  bookingTo?: string;
  matrixManifold?: MatrixManifold | string;
  onBehalfCompanyCode?: string;
  onBehalfCompanyName?: string;
  onBehalfCompanyNameEnglish?: string;
  onBehalfCompanyOther?: string;
  chargeablePartyCompanyCode?: string;
  chargeablePartyCompanyName?: string;
  chargeablePartyCompanyNameEnglish?: string;
  chargeablePartyOther?: string;
  operatorNeeded?: boolean;
  usageReason?: string;
  remarks?: string;
  operatorLicenseDocument?: TerminalServiceDocument | null;
  usageHours?: TerminalServiceUsageEntry[] | null;
  taskDecision?: TerminalServiceTaskDecision | null;
  task?: TerminalServiceTask | null;
  cancellationDetails?: TerminalServiceCancellationDetails | null;
  chargeablePartySameAsOnBehalf: boolean;
  attachments?: TerminalServiceDocument[] | null;
}

export interface BypassServiceDetailsData extends Record<string, unknown> {
  id?: string;
  status?: FottServiceRequestStatus | string;
  createdBy?: string;
  companyId?: string;
  createdByRole?: string;
  sourceDateTime?: string;
  vesselImo?: number | null;
  vesselName?: string | null;
  customerTerminal?: string;
  location?: string;
  operationType?: BypassOperationType;
  dockLine?: string;
  movNumber?: string;
  plta?: string | null;
  pldaNumber?: string | null;
  expectedClosureDate?: string;
  reasonForBypass?: string;
  remarks?: string | null;
  physicalVerificationOfValveTag?: boolean;
  collectionOfValveKeyFromFottCcr?: boolean;
  removalOfPadlockWitnessedByFottOperator?: boolean;
  crossCheckCommunication?: boolean;
  confirmationOfInstructionsWithCustomerCcr?: boolean;
  confirmationOfInstructionsWithFottCcr?: boolean;
  modifiedAt?: string;
  modifiedBy?: string | null;
  createdAt?: string;
  refOrderNumber?: number;
  attachments?: TerminalServiceDocument[] | null;
  closureDetails?: {
    remarks?: string | null;
    attachment?: TerminalServiceDocument | null;
    createdAt?: string | null;
    createdBy?: string | null;
  } | null;
  closureDate?: string | null;
  closedAt?: string | null;
  closedBy?: string | null;
  closureRemarks?: string | null;
  closureAttachment?: TerminalServiceDocument | null;
  closureDocument?: TerminalServiceDocument | null;
  cancellationDetails?: TerminalServiceCancellationDetails | null;
  task?: TerminalServiceTask | null;
  taskDecision?: TerminalServiceTaskDecision | null;
  chargeablePartySameAsOnBehalf?: boolean;
}

export interface NitrogenSupplyServiceDetailsData extends Record<string, unknown> {
  id?: string;
  status?: FottServiceRequestStatus | string;
  startTime?: string;
  usageHours?: number | null;
  usageHoursFott?: TerminalServiceUsageEntry[] | null;
  onBehalfCompanyCode?: string;
  onBehalfCompanyOther?: string | null;
  chargeablePartyCompanyCode?: string;
  chargeablePartyOther?: string | null;
  customerJlSlotSize?: string;
  fottDockLineNoSize?: string;
  usageReason?: string;
  remarks?: string;
  attachments?: TerminalServiceDocument[] | null;
  taskDecision?: TerminalServiceTaskDecision | null;
  task?: TerminalServiceTask | null;
  cancellationDetails?: TerminalServiceCancellationDetails | null;
  chargeablePartySameAsOnBehalf?: boolean;
}

export interface PiggingServiceDetailsData extends Record<string, unknown> {
  id?: string;
  status?: FottServiceRequestStatus | string;
  startingFrom?: string;
  onBehalfCompanyCode?: string;
  onBehalfCompanyName?: string;
  onBehalfCompanyNameEnglish?: string;
  onBehalfCompanyOther?: string | null;
  chargeablePartyCompanyCode?: string;
  chargeablePartyCompanyName?: string;
  chargeablePartyCompanyNameEnglish?: string;
  chargeablePartyOther?: string | null;
  customerJlNo?: string;
  customerTankNo?: string;
  fottDockLine?: string;
  productType?: string;
  usageReason?: string;
  remarks?: string;
  attachments?: TerminalServiceDocument[] | null;
  taskDecision?: TerminalServiceTaskDecision | null;
  task?: TerminalServiceTask | null;
  cancellationDetails?: TerminalServiceCancellationDetails | null;
  usageHours?: TerminalServiceUsageEntry[] | null;
}

export interface TerminalServiceDetailsResponse extends Record<string, unknown> {
  id?: string;
  serviceId?: number;
  serviceType?: FottServiceType | string;
  status?: FottServiceRequestStatus | string;
  serviceDetails?:
    | GantryCraneServiceDetailsData
    | NitrogenSupplyServiceDetailsData
    | BypassServiceDetailsData
    | PiggingServiceDetailsData
    | null;
}

export interface CreateGantryCraneServiceRequestPayload {
  bookingFrom: string;
  bookingTo: string;
  matrixManifold: MatrixManifold;
  onBehalfCompanyCode: string;
  onBehalfCompanyOther: string;
  chargeablePartyCompanyCode: string;
  chargeablePartyOther: string;
  operatorNeeded: boolean;
  usageReason: string;
  remarks: string;
  operatorLicenseDocument: TerminalServiceDocument | null;
  chargeablePartySameAsOnBehalf: boolean;
  attachments: TerminalServiceDocument[] | null;
}

export interface CreateNitrogenSupplyServiceRequestPayload {
  startTime: string;
  usageHours?: number;
  onBehalfCompanyCode: string;
  onBehalfCompanyOther: string | null;
  chargeablePartyCompanyCode: string;
  chargeablePartyOther: string | null;
  chargeablePartySameAsOnBehalf: boolean;
  customerJlSlotSize: string;
  fottDockLineNoSize: string;
  usageReason?: string;
  remarks?: string;
  attachments: TerminalServiceDocument[] | null;
  status: typeof FottServiceRequestStatus.PENDING_APPROVAL;
}

export interface CreatePiggingServiceRequestPayload {
  startingFrom: string;
  onBehalfCompanyCode: string;
  onBehalfCompanyOther: string;
  chargeablePartyCompanyCode: string;
  chargeablePartyOther: string;
  customerJlNo: string;
  customerTankNo: string;
  fottDockLine: string;
  productType: PiggingProductType;
  usageReason: string;
  remarks: string;
  attachments: TerminalServiceDocument[] | null;
  chargeablePartySameAsOnBehalf: boolean;
}

export interface CreateBypassServiceRequestPayload {
  sourceDateTime: string;
  vesselImo?: number;
  vesselName?: string;
  customerTerminal: string;
  location: string;
  operationType: string;
  dockLine: string;
  movNumber: string;
  plta?: string;
  pldaNumber?: string;
  expectedClosureDate: string;
  reasonForBypass: string;
  remarks?: string;
  physicalVerificationOfValveTag: boolean;
  collectionOfValveKeyFromFottCcr: boolean;
  removalOfPadlockWitnessedByFottOperator: boolean;
  crossCheckCommunication: boolean;
  confirmationOfInstructionsWithCustomerCcr: boolean;
  confirmationOfInstructionsWithFottCcr: boolean;
  attachments?: BypassServiceDocument[] | null;
}

export interface FottServiceResponse {
  items: FottService[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
}

export interface FottService {
  id: string;
  serviceId: number;
  serviceType: FottServiceType;
  bookingRequestedFrom: string;
  bookingRequestedTo: string | null;
  onBehalf: string;
  chargeableParty: string;
  numberOfUsageHours: number | null;
  status: FottServiceRequestStatus;
  taskId?: string | null;
  taskStatus?: string | null;
  taskClaimOwnerName?: string | null;
}
export interface FottServiceSearchRequestPayload {
  serviceId?: number[];
  serviceType?: FottServiceType[];
  bookingRequestedFrom?: Array<{
    from?: string;
    to?: string;
  }>;
  bookingRequestedTo?: Array<{
    from?: string;
    to?: string;
  }>;
  onBehalfCompanyCode?: string[];
  chargeablePartyCompanyCode?: string[];
  numberOfUsageHours?: Array<{
    from?: number | null;
    to?: number | null;
    includeNull?: boolean;
  }>;
  status?: FottServiceRequestStatus[];
  page?: number;
  pageSize?: number;
}
